import { Modal } from 'antd'
import { useForm, useWatch } from 'antd/lib/form/Form'
import { globalToaster } from 'components/elements/global'
import TaskFormBlock from 'components/shared/Modals/TaskFormBlock'
import {
 useUpdateLeadEventMutation,
 useUpdateRecurringEventMutation,
} from 'features/leads/api'
import {
 TAddLeadEventBody,
 TRecurringTaskData,
 TTaskEventActionPost,
} from 'features/leads/types'
import { baseTime } from 'lib'
import React, { FC, useEffect } from 'react'
import { checkIsDeferentTimeIsPast } from 'utils/functions'

const RecurringTaskEditModal: FC<{
 editableType?: 'single' | 'multiple'
 filterInfo?: string
 selectedType?: any
 onSuccess?: () => void
 selectedRows?: any[]
 selectAll?: boolean
 unselected?: any[]
 editData: TRecurringTaskData
 leadId?: any
 onCancel?: () => void
 visible?: boolean
}> = ({
 visible,
 onCancel,
 leadId,
 editData,
 selectAll,
 unselected,
 selectedRows,
 onSuccess,
 selectedType,
 filterInfo,
 editableType,
}) => {
 const [updateEvent, { isLoading }] = useUpdateRecurringEventMutation()
 const [updateLeadEvent, { isLoading: updateLoading }] =
  useUpdateLeadEventMutation()
 const [form] = useForm()

 const watch = useWatch([], form)

 const handleSubmit = async () => {
  const {
   end_value_after,
   end_value_specific,
   weekly_days,
   selected_week,
   monthly_date,
   monthly_day,
   monthly_selected_type,
   selected_type,
   lead_id,
   ...values
  } = await form.validateFields()

  if (baseTime(values.scheduled_at).isBefore(baseTime())) {
   globalToaster({
    type: 'error',
    content: 'Please  select a valid date to continue',
   })
  } else {
   const postData: TTaskEventActionPost = {
    ...values,

    date: baseTime(values.scheduled_at).format('YYYY-MM-DD'),
    time: baseTime(values.scheduled_at).format('HH:mm:ss'),
    lead_id: leadId,
    // feed: 'task',
    recurring_events_id: editData?.recurring_events_id,
    ...(watch?.repeat_event
     ? {
        selected_type,
        ...(selected_type === 'monthly'
         ? {
            selected_week,
            monthly_selected_type,
            ...(monthly_selected_type === 'date'
             ? { monthly_date }
             : { monthly_day }),
           }
         : {}),

        end:
         values?.end_type === 'after'
          ? `${end_value_after}`
          : baseTime(end_value_specific).format('YYYY-MM-DD'),
        weekly_days: JSON.stringify(weekly_days),
        editable: 'multiple',
       }
     : {}),
    action: 'update',
    ...(selectAll
     ? {
        ...(unselected && unselected?.length > 0 ? { unselected } : {}),
        select_all: true,
        select_all_type: selectedType,
        ...(filterInfo ? { filter_info: filterInfo } : {}),
       }
     : { task_id: selectedRows }),
   }
   if (selectedRows?.length === 1) {
    await updateLeadEvent({
     ...values,
     date: baseTime(values.scheduled_at).format('YYYY-MM-DD'),
     time: baseTime(values.scheduled_at).format('HH:mm:ss'),
     lead_id: leadId,
     feed: 'task',
     recurring_events_id: editData?.recurring_events_id,
     id: selectedRows[0],
    }).unwrap()
   } else {
    await updateEvent(postData).unwrap()
   }

   form.resetFields()
   onSuccess?.()
   onCancel?.()
  }
 }

 useEffect(() => {
  if (editData) {
   const { recurring } = editData
   const dateTime = baseTime(`${editData.date} ${editData.time}`)
   form.setFieldsValue({
    title: editData.name,
    type: editData.type,
    ...(editData.reminder &&
    checkIsDeferentTimeIsPast(dateTime, editData.reminder)
     ? { reminder: editData.reminder }
     : {}),
    scheduled_at: dateTime,
    lead_id: [{ title: editData?.lead_name, id: editData?.lead_id }] as any,
    // lead_id: editData.lead_id,
    ...(editData?.is_recurring
     ? {
        repeat_event: editData?.is_recurring === 1,
        difference_month: recurring?.difference_month,
        selected_type: recurring?.selected_type,
        end_type: recurring?.end_type,
        monthly_selected_type: recurring?.monthly_selected_type ?? 'date',
        ...(recurring?.end_date && recurring?.end_type
         ? {
            end_value_after:
             recurring?.end_type === 'after' ? Number(recurring?.end_date) : 1,
            end_value_specific:
             recurring?.end_type === 'specific_date'
              ? (baseTime(recurring?.end_date) as any)
              : undefined,
           }
         : {}),
        ...(recurring?.selected_type === 'weekly' &&
         recurring?.weekly_days && {
          weekly_days: JSON.parse(recurring?.weekly_days ?? ''),
         }),
        ...(recurring?.selected_type === 'monthly' && {
         selected_week: recurring?.selected_week,
         monthly_day:
          recurring?.monthly_selected_type === 'day'
           ? recurring?.monthly_day
           : baseTime().format('dddd'),
         monthly_date:
          recurring?.monthly_selected_type === 'date'
           ? Number(recurring?.monthly_date ?? 0)
           : Number(baseTime().format('DD') ?? 1),
        }),
       }
     : {}),
   })
  } else {
   form.resetFields()
  }
  return () => {
   form.resetFields()
  }
 }, [editData])

 return (
  <Modal
   width={700}
   okText="Update"
   onOk={handleSubmit}
   confirmLoading={isLoading || updateLoading}
   onCancel={onCancel}
   visible={visible}
   title="Edit Task"
  >
   <TaskFormBlock
    isRecurringTask={editData?.is_recurring === 1}
    mode="EDIT"
    editableType={editableType}
    leadId={leadId}
    form={form}
   />
  </Modal>
 )
}

export default RecurringTaskEditModal
